import React from 'react';
import classnames from 'classnames';
import { useIntl, history } from 'umi';
import { Layout, Card, Justify, PagingQuery } from 'tea-component';

import Abnormal from '@/pages/intelligence/components/Abnormal';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Visualis from './components/Visualis';
import Tables from './components/Tables';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';

// import { fetchStoreRankMoreRanks } from '@/pages/intelligence/services/storeRank';
import { fetchStoreRankMoreRanks } from './services';

import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';

import '@/pages/intelligence/assets/scss/StoreRanks/index.scss';

import type { IBaseProps, IGameType } from '@/pages/intelligence/types/common';
import type {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type {
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreRankPrimaryKey,
  IStoreRankMoreCoreRankUnifiedKey,
  IStoreRankMoreCoreRank,
  IStoreRankMoreTableDimensionInfos,
} from '@/pages/intelligence/types/storeRank';
import type { IOptionWithChildren } from '@/pages/intelligence/components/GenreSelect/types';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import { AppMagicGenreTagType } from '@/pages/intelligence/components/TableComponents/components/AppMagicGenreTagList';

const { Content } = Layout;
const SEARCH_TYPE = 'mobile-store-rank-more';
interface IStoreRankMoreCoreProps extends IBaseProps {
  entityType: IGameType;
  searchType: 'mobile-store-rank-more' | 'pc-store-rank-more' | 'console-store-rank-more';
  isSectional: boolean;
  isShowVisualis: boolean;
  isOperate: boolean;
  title?: string | React.ReactElement;
  filter?: React.ReactElement;
  exportTable?: React.ReactElement;
  pageLogInfo?: any;

  fetchParams: IStoreRankMoreCoreFetchParams;

  visualisOptions: {
    isVisualisLoading: boolean;
    isVisualisNoData: boolean;
    seriesData: any[];
    xAxisData: any[];
    sortKeyName: string;
    currentLine: any;
    segments: any[];
    segment: string;
    setSegment: React.Dispatch<React.SetStateAction<string>>;
    yAxisFormatter: (payload: any) => string;
    tooltipFormatter: (payload: any) => string;
    createLegendsCustomMade: any;
  };
  tablesOptions: {
    isTableLoading: boolean;
    isTableNoData: boolean;
    imageKey: string;
    primaryKey: IStoreRankMoreCoreRankPrimaryKey;
    uunifiedKey: IStoreRankMoreCoreRankUnifiedKey;
    sectionalRankKeys: string[];
    sectionalRankInfos: {
      [key: string]: IStoreRankMoreTableDimensionInfos;
    };
    platformTopMetrics: string[];
    platformTopInfos: {
      [key: string]: IStoreRankMoreTableDimensionInfos;
    };
    tableDimensionsData: string[];
    tableTotal: number;
    tableData: IStoreRankMoreCoreRank[];
    tableSelection: TableSelectionType[];
    defaultSelectionGames: any[];
    transTableDataForSearch: any;
    getTableSearchList: any;
    handleClearSearch: any;
    // 判断表格内 details按钮是否禁用、以及禁用时的提示文案
    getDetailsBtnDisabledInfo?: (cvm: IStoreRankMoreCoreRank) => {
      disabled?: boolean;
      disabledMsg?: string | React.ReactElement;
    };
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
    // App Magic 品类标签点击回调
    onAppMagicTagClick?: (type: AppMagicGenreTagType, superTag: string, majorTag?: string, midTag?: string, subTag?: string) => void;
    onTableSortChage: (payload: SortByItemType) => void;
    onTablePagingChange: (payload: PagingQuery) => void;
    onTableCheckGame: (selection: TableSelectionType[], checkStatus: TableSelectStatusType) => void;
  };
}

const StoreRankMoreCore: React.FC<IStoreRankMoreCoreProps> = (props) => {
  const {
    entityType = 'mobile',
    searchType,
    isSectional,
    isShowVisualis,
    isOperate,
    title,
    filter,
    exportTable,
    pageLogInfo,
    isHasDetailsCloums = true,
    fetchParams,
    visualisOptions,
    tablesOptions,
  } = props;

  const {
    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,
  } = visualisOptions ?? {};

  const {
    isTableLoading,
    // isTableNoData,
    imageKey,
    primaryKey,
    uunifiedKey,
    sectionalRankKeys = [],
    sectionalRankInfos = {},
    platformTopMetrics = [],
    platformTopInfos = {},
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableSearchList,
    handleClearSearch,
    getDetailsBtnDisabledInfo,
    genreOptionsMap,
    onAppMagicTagClick,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
  } = tablesOptions ?? {};

  const { formatMessage } = useIntl();

  const isVisualisLoadingTmp = isSectional ? isTableLoading : isVisualisLoading;
  const isVisualisNoDataTmp = isSectional
    ? !(tableData?.length > 0 && tableSelection?.length > 0)
    : isVisualisNoData;

  return (
    <Content
      className={classnames('dbi-mobileglobalregion-more dbi-storeranks-more', {
        'is-sectional': isSectional,
        // mobile store rank下钻页 勾选多国家sectional模式时 表格没有勾选交互，其余情况表格都有勾选交互
        'table-selectable': !(entityType === 'mobile' && isSectional),
      })}
    >
      <Content.Header
        title={title}
        showBackButton
        onBackButtonClick={() => {
          history.goBack();
        }}
      />
      <Content.Body full>
        {/* 筛选器 */}
        {React.isValidElement(filter) && filter}
        {isShowVisualis && (
          <Card className="dbi-mobileGames-card-context">
            <Card.Body
              title={
                <div style={{ display: 'flex' }}>
                  <span style={{ marginRight: 10 }}>{title}</span>
                  {!isTableLoading && !isVisualisNoDataTmp && (
                    <ArrowdownText
                      isNew={true}
                      sortedData={sortKeyName}
                      order={fetchParams?.order}
                    />
                  )}
                </div>
              }
            >
              <Abnormal
                isLoading={isVisualisLoadingTmp}
                isNoData={false}
                component={
                  <Visualis
                    entityType={entityType}
                    chartType={isSectional ? ChartType.Bar : ChartType.Line}
                    titleNow={formatMessage({ id: 'dbi_rank' })}
                    isLoading={isVisualisLoadingTmp}
                    isNoData={isVisualisNoDataTmp}
                    isShowSortInfo={!isTableLoading}
                    dateType={fetchParams?.granularity}
                    order={fetchParams?.order}
                    seriesData={seriesData}
                    xAxisData={xAxisData}
                    yAxisFormatter={yAxisFormatter}
                    tooltipFormatter={tooltipFormatter}
                    createLegendsCustomMade={createLegendsCustomMade}
                    currentLine={currentLine}
                    segments={segments}
                    segment={segment}
                    setSegment={setSegment}
                    imageKey={imageKey}
                    primaryKey={primaryKey}
                    sectionalRankKeys={sectionalRankKeys}
                    sortKeyName={sortKeyName}
                    tableSelection={tableSelection}
                    tooltipCfg={{
                      headerLeftTitle: fetchParams?.start_date || fetchParams?.end_date,
                    }}
                  />
                }
              />
            </Card.Body>
          </Card>
        )}
        <Justify
          className="dbi-card-justify"
          left={
            searchType === SEARCH_TYPE ? (
              <SearchRecent
                searchType={searchType}
                pageLogInfo={pageLogInfo}
                fetchParams={{ ...fetchParams, page: 1 }}
                searchListApi={fetchStoreRankMoreRanks}
                initSearchInput={!(fetchParams?.search_ids?.length > 0)}
                transTableDataForSearch={transTableDataForSearch}
                getTableResultList={getTableSearchList}
                handleClear={handleClearSearch}
              />
            ) : null
          }
          right={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {searchType === SEARCH_TYPE ? null : (
                <SearchRecent
                  searchType={searchType}
                  pageLogInfo={pageLogInfo}
                  fetchParams={{ ...fetchParams, page: 1 }}
                  searchListApi={fetchStoreRankMoreRanks}
                  initSearchInput={!(fetchParams?.search_ids?.length > 0)}
                  transTableDataForSearch={transTableDataForSearch}
                  getTableResultList={getTableSearchList}
                  handleClear={handleClearSearch}
                />
              )}
              {searchType === SEARCH_TYPE ? (
                <span style={{ fontSize: '14px', marginRight: '10px', color: '#838697' }}>
                  {formatMessage({ id: 'dbi_store_rank_download_tips_title' })}
                </span>
              ) : null}
              {/* 下载按钮 */}
              {React.isValidElement(exportTable) && exportTable}
            </div>
          }
        />
        <CardSlot>
          <Tables
            entityType={entityType}
            topChartsName={fetchParams?.topchart_name}
            isSectional={isSectional}
            isSelectable={isShowVisualis}
            isOperate={isOperate}
            primaryKey={primaryKey}
            uunifiedKey={uunifiedKey}
            sectionalRankKeys={sectionalRankKeys}
            sectionalRankInfos={sectionalRankInfos}
            platformTopMetrics={platformTopMetrics}
            platformTopInfos={platformTopInfos}
            tableDimensionsData={tableDimensionsData}
            records={tableData}
            loading={isTableLoading}
            tableTotal={tableTotal}
            isHasDetailsCloums={isHasDetailsCloums}
            pageSortAttr={{
              tablePageIndex: fetchParams.page,
              tablePageSize: fetchParams.page_size,
              currentOrderSource: fetchParams?.order_source,
              currentSortMetric: fetchParams.order_metric,
              currentSortValue: fetchParams.order,
              currentRatioType: fetchParams?.ratio_type,
            }}
            onTableSortChage={onTableSortChage}
            onTablePagingChange={onTablePagingChange}
            onTableCheckGame={onTableCheckGame}
            tableSelection={tableSelection}
            tableSelectionAll={defaultSelectionGames}
            genreSource={fetchParams?.genre_source ?? 'annie'}
            genreOptionsMap={genreOptionsMap}
            getDetailsBtnDisabledInfo={getDetailsBtnDisabledInfo}
            onAppMagicTagClick={onAppMagicTagClick}
          />
        </CardSlot>
      </Content.Body>
    </Content>
  );
};

export default StoreRankMoreCore;
